import { ColumnDef } from "@tanstack/react-table";
import { TrashAltIcon } from "assets/icons";
import cn from "classnames";
import { useLanguage } from "hooks";
import { t } from "i18next";
import moment from "moment";
/* eslint-disable react-hooks/exhaustive-deps */
import { useMemo, useState } from "react";
import {
  DateTableCell,
  ItemsTable,
  PersonInfo,
  Status,
  TableItemActionsControl,
  TableItemStatus,
  TableItemStatusControl
} from "ui";

import { useCouponStatusChange } from "../../../apis";
import {
  COUPON_STATUSES_LIST,
  CouponStatus,
  CouponType
} from "../../../constants";
import { CouponItem } from "../../../coupons.types";
import { CustomCouponsTableProps } from "./InfluencerCouponsTable.types";

import styles from "./InfluencerCouponsTable.module.scss";

const InfluencerCouponsTable = ({
  coupons,
  sorting,
  setSorting,
  isLoading,
  onDeleteCoupon
}: CustomCouponsTableProps) => {
  const changeCouponStatus = useCouponStatusChange(CouponType.InfluencerCoupon);
  const { currentLanguage } = useLanguage();

  const [openedItemOptionsIndex, setOpenedItemOptionsIndex] = useState<
    number | null
  >(null);

  const [openedItemStatusesIndex, setOpenedItemStatusesIndex] = useState<
    number | null
  >(null);

  const statusesList: Status[] = COUPON_STATUSES_LIST.map((orStatus) => ({
    ...orStatus,
    onClick: () => {
      if (openedItemStatusesIndex !== null) {
        changeCouponStatus.mutate({
          couponId: coupons[openedItemStatusesIndex].couponId,
          action:
            orStatus.value === CouponStatus.Active ? "activate" : "deactivate"
        });
        setOpenedItemStatusesIndex(null);
      }
    }
  }));

  const columns: Array<ColumnDef<CouponItem, CouponItem[]>> = useMemo(
    () => [
      {
        header: t("Coupon Name"),
        size: 20,
        minSize: 20,
        cell: ({ row }) => (
          <div className={styles.table_codeCell}>
            <p className={styles.table_boldText}>{row.original.code}</p>

            {/* Permanent codes link customers for life; campaign codes do not.
                Telling them apart at a glance matters here. */}
            <span
              className={cn(styles.table_codeKind, {
                [styles.table_codeKind__permanent]:
                  !!row.original.metadata?.isPermanentInfluencerCode
              })}
            >
              {row.original.metadata?.isPermanentInfluencerCode
                ? t("Permanent")
                : t("Limited-time")}
            </span>
          </div>
        )
      },
      {
        header: t("Influencer"),
        size: 20,
        minSize: 20,
        cell: ({ row }) => (
          <PersonInfo
            wrapperClassName={styles.table_personInfo}
            nameClassName={styles.table_personInfo_name}
            dimensions={{ width: 30, height: 30, borderRadius: "100%" }}
            personName={`${row.original.influencerWhoGivenInfluencerCoupon?.firstname} ${row.original.influencerWhoGivenInfluencerCoupon?.lastname}`}
            image={row.original.influencerWhoGivenInfluencerCoupon?.image}
          />
        )
      },
      {
        header: t("Discount"),
        size: 15,
        minSize: 15,
        cell: ({ row }) =>
          row.original.discountPercentage
            ? `${row.original.discountPercentage}%`
            : `${row.original.discountAmount?.toLocaleString("en-US")} FCFA`
      },
      {
        header: t("From Date"),
        size: 15,
        minSize: 15,
        cell: ({ row }) => <DateTableCell date={row.original.from} />
      },
      {
        header: t("To Date"),
        size: 15,
        minSize: 15,
        cell: ({ row }) =>
          row.original.to ? (
            <DateTableCell date={row.original.to} />
          ) : (
            t("Unlimited")
          )
      },
      {
        header: t("Status"),
        size: 13,
        minSize: 13,
        cell: ({ row }) => {
          const canChangeStatus =
            !row.original.from ||
            !row.original.to ||
            moment().isBetween(
              moment(row.original.from),
              moment(row.original.to)
            );

          return canChangeStatus ? (
            <TableItemStatusControl
              rowIndex={row.index}
              activeStatusIndex={
                row.original.status === CouponStatus.Active ? 0 : 1
              }
              openedItemStatusesIndex={openedItemStatusesIndex}
              setOpenedItemStatusesIndex={setOpenedItemStatusesIndex}
              statusesList={statusesList}
            />
          ) : (
            <TableItemStatus
              activeStatusIndex={
                row.original.status === CouponStatus.Active ? 0 : 1
              }
              statusesList={statusesList}
            />
          );
        }
      },

      {
        accessorKey: "dotsColumn",
        header: () => null,
        size: 2,
        minSize: 2,
        cell: ({ row }) => (
          <TableItemActionsControl
            actionsList={[
              {
                label: "Delete",
                Icon: <TrashAltIcon fill={"#ff0033"} width={18} height={18} />,
                labelColor: "#ff0033",
                onClick: () => onDeleteCoupon(row.original.couponId)
              }
            ]}
            rowIndex={row.index}
            setOpenedItemOptionsIndex={setOpenedItemOptionsIndex}
            openedItemOptionsIndex={openedItemOptionsIndex}
          />
        ),
        enableSorting: false
      }
    ],
    [coupons, openedItemOptionsIndex, openedItemStatusesIndex, currentLanguage]
  );

  return (
    <>
      <ItemsTable
        columns={columns}
        data={coupons}
        sorting={sorting}
        setSorting={setSorting}
        isLoading={isLoading}
        className={styles.table}
      />
    </>
  );
};

export default InfluencerCouponsTable;
